/**
 * Morceaux partagés par les deux écrans d'audit (alertes de sécurité et journal
 * des actions): pastilles, fenêtres, détail d'une action, activité d'un
 * utilisateur sur 24 heures.
 */
import React, { useCallback, useState } from 'react';
import { AlertTriangle, TrendingUp, X } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';
import { humaniser, libelle, nomLisible, texteSur } from '../../lib/libelles';
import { CHAMPS_FR, messageErreur } from '../../lib/erreurs';
import { formatDateHeure, formatNombre } from '../../lib/formats';

export const EMPTY_ARRAY = [];

export const fmtDate = (d) => formatDateHeure(d);

// Nom lisible d'un champ modifié: jamais le nom technique.
export const nomChamp = (cle) => CHAMPS_FR[cle] || humaniser(cle) || 'Autre information';

// Valeur lisible d'un champ modifié.
export const valeurLisible = (valeur) => {
  if (valeur === null || valeur === undefined || valeur === '') return '—';
  if (typeof valeur === 'boolean') return valeur ? 'Oui' : 'Non';
  if (typeof valeur === 'number') return formatNombre(valeur, Number.isInteger(valeur) ? 0 : 2);
  if (Array.isArray(valeur)) return valeur.length ? valeur.map(valeurLisible).join(', ') : '—';
  if (typeof valeur === 'object') {
    const morceaux = Object.entries(valeur).map(([cle, v]) => `${nomChamp(cle)} : ${valeurLisible(v)}`);
    return morceaux.length ? morceaux.join(' · ') : '—';
  }
  return texteSur(valeur);
};

export const Badge = ({ children, color = 'slate' }) => {
  const colors = {
    slate: 'bg-muted text-muted-foreground',
    emerald: 'bg-success/15 text-success',
    rose: 'bg-destructive/15 text-destructive',
    amber: 'bg-flame/15 text-flame-ink',
    sky: 'bg-info/15 text-info',
    violet: 'bg-process/15 text-process',
  };
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${colors[color]}`}>
      {children}
    </span>
  );
};

export const actionColor = (action = '') => {
  if (/DELETE|REMOVED|REVOKED|SUSPENDED|FAILED/.test(action)) return 'rose';
  if (/CREATED|LOGIN_SUCCESS|REACTIVATED/.test(action)) return 'emerald';
  if (/UPDATED|CHANGED|RENEWED/.test(action)) return 'amber';
  if (/BACKEND_OP/.test(action)) return 'violet';
  return 'slate';
};

export const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
      {label}
    </label>
    {children}
  </div>
);

export const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="w-full max-w-2xl rounded-2xl bg-card shadow-2xl">
      <div className="flex items-center justify-between border-b border-border p-4">
        <h3>{title}</h3>
        <button onClick={onClose} className="rounded-full p-1 hover:bg-muted" aria-label="Fermer">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

export const KV = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-3 border-b border-border/60 py-1.5 last:border-0">
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className={`text-sm text-foreground text-right ${mono ? 'font-mono' : ''}`}>{value}</span>
  </div>
);

// Lignes « information / avant / après » à partir des trois formes que le serveur enregistre:
// { before, after }, { champ: { from, to } } ou un simple dictionnaire.
const lignesDeChangement = (changes) => {
  const estObjet = (v) => v && typeof v === 'object' && !Array.isArray(v);
  if (changes.before || changes.after) {
    const avant = estObjet(changes.before) ? changes.before : {};
    const apres = estObjet(changes.after) ? changes.after : {};
    const cles = Array.from(new Set([...Object.keys(avant), ...Object.keys(apres)]));
    return cles.map((cle) => ({ cle, avant: avant[cle], apres: apres[cle] }));
  }
  return Object.entries(changes).map(([cle, valeur]) =>
    estObjet(valeur) && ('from' in valeur || 'to' in valeur)
      ? { cle, avant: valeur.from, apres: valeur.to }
      : { cle, avant: undefined, apres: valeur },
  );
};

export const DiffView = ({ changes }) => {
  if (!changes || Object.keys(changes).length === 0) {
    return <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground/70">Aucune modification enregistrée</div>;
  }
  const lignes = lignesDeChangement(changes);
  return (
    <div className="max-h-60 overflow-auto rounded-lg border border-border/60">
      <table className="w-full text-xs">
        <thead className="bg-muted/50 text-left text-muted-foreground">
          <tr>
            <th className="px-2 py-1.5">Information</th>
            <th className="px-2 py-1.5">Avant</th>
            <th className="px-2 py-1.5">Après</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {lignes.map((ligne) => (
            <tr key={ligne.cle}>
              <td className="px-2 py-1.5 font-medium text-foreground">{nomChamp(ligne.cle)}</td>
              <td className="px-2 py-1.5 text-destructive">{valeurLisible(ligne.avant)}</td>
              <td className="px-2 py-1.5 text-success">{valeurLisible(ligne.apres)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Activité d'un utilisateur sur 24 heures. Renvoie la fonction qui ouvre la
 * fenêtre et l'élément à afficher dans la page.
 */
export function useActiviteUtilisateur() {
  const [userTimeline, setUserTimeline] = useState(null);

  const ouvrir = useCallback(async (userId, nom) => {
    try {
      const data = await adminAPI.getUserTimeline(userId, 24);
      setUserTimeline({ userId, nom: nomLisible(nom, 'cet utilisateur'), logs: data || [] });
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de charger l’activité de cet utilisateur'));
    }
  }, []);

  const fenetre = userTimeline ? (
    <Modal onClose={() => setUserTimeline(null)} title={`Activité des dernières 24 h : ${userTimeline.nom}`}>
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {userTimeline.logs.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground/70">Aucune activité sur les dernières 24 h</div>
        ) : (
          userTimeline.logs.map((log) => (
            <div key={log.id || log._id || log.timestamp || ''} className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/50 p-2">
              <div className="flex items-center gap-2">
                <Badge color={actionColor(log.action)}>{libelle('auditAction', log.action)}</Badge>
                <span className="text-xs text-muted-foreground">{libelle('auditEntity', log.entity_type)}</span>
              </div>
              <span className="font-mono text-xs text-muted-foreground/70">{fmtDate(log.timestamp)}</span>
            </div>
          ))
        )}
      </div>
    </Modal>
  ) : null;

  return [ouvrir, fenetre];
}

/** Détail d'une action du journal. */
export const DetailAction = ({ log, onClose, onVoirUtilisateur }) => (
  <Modal onClose={onClose} title="Détail de l’action">
    <div className="space-y-3 text-sm">
      <KV label="Date et heure" value={fmtDate(log.timestamp)} />
      <KV label="Action" value={<Badge color={actionColor(log.action)}>{libelle('auditAction', log.action)}</Badge>} />
      <KV label="Élément" value={libelle('auditEntity', log.entity_type)} />
      <KV label="Fait par" value={nomLisible(log.changed_by_name, 'Système')} />
      <KV label="Organisation" value={nomLisible(log.organization_name, '—')} />
      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Ce qui a changé
        </div>
        <DiffView changes={log.changes} />
      </div>
      {log.changed_by_id && (
        <button
          onClick={() => onVoirUtilisateur(log.changed_by_id, log.changed_by_name)}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          Voir toute l'activité de cet utilisateur
        </button>
      )}
    </div>
  </Modal>
);

export const AnomalyCard = ({ title, subtitle, items, color, onItemClick }) => {
  const bg = {
    rose: 'border-destructive/30 bg-destructive/10',
    amber: 'border-flame/30 bg-flame/10',
  }[color];
  const dot = { rose: 'bg-destructive', amber: 'bg-flame' }[color];
  return (
    <div className={`rounded-2xl border ${bg} p-4`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className={`h-4 w-4 text-${color}-600`} />
          <div>
            <div className="text-sm font-bold text-foreground">{title}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{subtitle}</div>
          </div>
        </div>
        <div className={`rounded-full ${dot} px-2 py-0.5 text-xs font-bold text-white`}>
          {items?.length || 0}
        </div>
      </div>
      {items && items.length > 0 ? (
        <div className="space-y-1">
          {items.slice(0, 5).map((item) => (
            <button
              key={item._id || item.name || item.changed_by_id || ''}
              onClick={() => onItemClick?.(item)}
              className="flex w-full items-center justify-between rounded-md bg-card px-2 py-1.5 text-xs hover:bg-muted/50"
            >
              <span className="truncate font-medium text-muted-foreground">
                {nomLisible(item.name, 'Utilisateur inconnu')}
              </span>
              <span className="font-bold text-foreground">{formatNombre(item.count)}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground/70">Rien à signaler</div>
      )}
    </div>
  );
};

export const AnalyticsCard = ({ title, items = EMPTY_ARRAY, color, labelKey, domaine }) => {
  const max = Math.max(...items.map((i) => i.count || 0), 1);
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="uppercase text-muted-foreground">{title}</h3>
        <TrendingUp className="h-4 w-4 text-muted-foreground/70" />
      </div>
      <div className="space-y-1.5">
        {items.slice(0, 5).map((item) => {
          const label = domaine
            ? libelle(domaine, item._id)
            : nomLisible(labelKey ? item[labelKey] : null, 'Utilisateur inconnu');
          const pct = (item.count / max) * 100;
          return (
            <div key={item._id || item.name || item.count || ''}>
              <div className="flex items-center justify-between text-xs">
                <span className="truncate font-medium text-muted-foreground">{label}</span>
                <span className="ml-2 font-bold text-foreground">{formatNombre(item.count)}</span>
              </div>
              <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full bg-${color}-500`}
                  style={{ width: `${pct}%`, background: color === 'sky' ? 'hsl(var(--info))' : color === 'amber' ? 'hsl(var(--flame))' : 'hsl(var(--process))' }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
